import { DESIGN_OPTIONS } from 'constants/energyAdvisor';
import { UTILITIES } from 'constants/utilitiesMappers';
import { eden } from 'constants/mapCoordinates';
import { PAYMENT_GATEWAYS } from 'constants/payments';

import Colors from './colors/eden';
import SassColors from './eden/scss/_colors.scss';

const EMAIL = 'email';

const utilitySettings = {
  i18n: 'Eden',
  colors: Colors,
  sassColors: SassColors,
  assets: 'eden',
  appTitle: 'EDEN',
  utilityId: 10,
  mappers: UTILITIES.eden,
  includesHolderChangeProcedure: false,
  canChangePrimaryEmail: true,
  includesTerminationService: false,
  includesRegisterAccount: false,
  usePayments: false,
  usePaymentDocuments: true,
  usePoints: false,
  deleteAccount: true,
  shouldUseAccOptimization: true,
  ACCOUNT_LENGTH: 9,
  clientNumberRegex: /^[1-9]{1}[0-9]{8}$/,
  lineSections: true,
  showUserAccountRelationship: false,
  useClientSummary: false,
  useChatbot: false,
  canUseDashboardWithoutAccounts: false,
  fontFamily: 'Helvetica',
  mobileSidebarLogo: true,
  mobileTopbarLogo: false,
  disabledMobileAvatarEnergyAdvisor: false,
  UTModal: {
    modalBorderRadius: 7
  },
  turns: {
    active: process.env.REACT_APP_ENV !== 'prod'
  },
  // Service Adherence
  automaticDebitShortcuts: false,
  digitalBillShortcuts: true,
  userDigitalBillAdherence: true,
  digitalBillWithForm: false,
  // Sign in configuration
  signInMethod: EMAIL,
  passwordRequireNumberAndLetter: false,
  passwordMinLength: 6,
  passwordMaxLength: 20,
  PASSWORD_CHANGE_EXTERNAL_TOKEN: 'edelap_token', // TODO: Try to change this 'edelap_token' with something more generic ant put in signin constants
  hideAppTitle: true,
  authAvatarSquared: true,
  authAvatarWithShadow: false,
  authAvatarWithBackground: false,
  authBackgroundColorHorizontal: false,
  authImageBackground: true,
  authLogoInForm: true,
  authFormCentered: false,
  buttonRadius: 20,
  squaredToggle: false,
  payExternalAcc: false,
  errorScreenWithImage: false,
  analytics: {
    trackDigitalBillOnRegister: false
  },
  withTooltip: true,
  withAppDownloader: true,
  accountSelectKeys: [
    { prefix: 'NIS', key: 'client_number' },
    { prefix: '-', key: 'address' }
  ],
  appleStoreURL: 'https://itunes.apple.com/ar/app/eden-m%C3%B3vil/id1360325018?mt=8',
  googlePlayStoreURL: 'https://play.google.com/store/apps/details?id=com.utility.eden&hl=es_AR',
  googleMap: {
    defaultCoordinates: {
      latitude: -33.347352,
      longitude: -60.206085
    },
    colors: {
      polygon: Colors.primary,
      borderPolygon: Colors.primary
    },
    zoom: {
      initial: 15,
      addressValid: 15,
      addressInvalid: 10
    },
    withPolygon: false,
    withGeolocation: true,
    path: eden
  },
  signIn: {
    conditionalRequestGender: false,
    minDocNumber: 10000,
    maxDocNumber: 10000000,
    showSocialNetworks: true,
    socialNetworks: {
      linkedin: 'https://www.linkedin.com/company/empresa-distribuidora-de-energ%C3%ADa-norte-s-a-/',
      facebook: 'https://www.facebook.com/EDENComunica/',
      twitter: 'https://twitter.com/edensacomunica?lang=es'
    },
    iconField: false
  },
  // Sign up flow
  signUpFlow: {
    PersonalInformation: true,
    IdentityValidation: false,
    UserInformation: true,
    TermsAndConditions: false,
    EmailValidation: true,
    AddAccount: true,
    AccountRelationshipValidation: true,
    ServiceAdherence: true,
    RelatedAccounts: true
  },
  signUp: {
    retrieveUserIdentification: false,
    personalInformationFields: {
      document_type: { show: false, required: true },
      document_number: { show: false, required: true },
      gender: { show: false, required: true },
      name: { show: true, required: true },
      last_name: { show: true, required: true },
      bornDate: { show: true, required: true },
      carrier: { show: false, requiredOn: 'cellNumber' },
      cellNumber: { show: true, required: false, dependsOf: 'phone' },
      phone: { show: true, required: false, dependsOf: 'cellNumber' },
      contact_first_name: { show: false, required: true },
      platform_discovery: { show: true, required: true },
      contact_last_name: { show: false, required: true },
      captcha: { show: true, required: true }
    },
    personalInformationFullWidthFields: true,
    nameMaxLength: 20,
    lastNameMaxLength: 20
  },
  // Dashboard configuration
  showAmountChart: false,
  showAmountComparison: false,
  showAnnualProgress: false,
  topbarUtilityLogo: true,
  topbarCustomIcons: false,
  topbar: {},
  utilityLinks: [
    { text: 'Aviso Legal', url: 'https://www.edensa.com.ar/aviso-legal/' },
    { text: 'Politicas de privacidad', url: 'https://www.edensa.com.ar/privacidad/' }
  ],
  addSupplies: true,
  simulatorLink: 'https://simuladordeconsumo.edensa.com.ar/',
  simulatorButton: true,
  accountStatus: {
    consumptionHistory: true,
    consumptionHistoryPeriods: 13,
    digitalBillButtonWithTitle: true
  },

  // Bills configuration
  billsPageSize: 6,
  disableBillsPagination: true,
  PaymentDocumentSelection: false,
  encodedDocumentDownload: true,
  useBillsBalance: false,
  useBillsSurchargeAmount: true,
  multipleBillsDownload: false,
  claims: {
    useClaims: true,
    useClaimList: false,
    openableRow: true,
    openClaimsFromSidebar: false,
    showAccountSelector: true,
    cancelClaimComment: false,
    reiterationClaimComment: false,
    showClaimFormsOnSidebar: true
  },
  procedures: {
    useProcedures: true,
    useProcedureList: false,
    showAccountSelector: true
  },
  bills: {
    billDownloadOnPDF: false,
    lastBill: true,
    downloadIcon: false,
    downloadIconBillsScreen: true,
    downloadIconMobile: false,
    showAccountSelector: true
  },
  billsColumns: {
    period: { width: 0.07, order: 1 },
    issued_on: { width: 0.09, order: 2 },
    document_type: { width: 0.18, order: 3 },
    document_number: { width: 0.13, order: 4 },
    first_expiration_on: { width: 0.11, order: 5 },
    status: { width: 0.14, order: 6 },
    amount: { width: 0.11, order: 7 },
    balance: { width: 0.08, order: 8 },
    download: { width: 0.09, order: 9 },
    payable: { width: 0.09, order: 10 }
  },
  maxAmountThreshold: 999999999,
  maxDocsThreshold: 10,

  // Public payment configuration
  userDataRequired: true, // email or doc not required for coupon
  allowPaymentDocumentSelection: false, // if true, it goes to payment screen after pressing coupon button
  allowMultipleCoupons: false,
  showPaymentPlaces: false,

  // Settings view configuration
  userDataManagement: {
    landlineRequired: false,
    cellPhoneRequired: false,
    phoneRequired: true,
    defaultPhoneRequired: false,
    requirePasswordForUserDataManagement: false,
    useCarrier: false,
    useDefaultPhone: false,
    shouldUpdateEmail: false,
    mayDeletePhones: false,
    MAX_PHONES: 1,
    usePhoneFields: true,
    emailMaxLength: 255,
    rowDescIcons: true,
    addPhoneButton: false,
    userConfigBlueMargin: false
  },

  dashboard: {
    pendingFineAmountQuery: false,
    pendingWarrantyDepositQuery: false
  },

  publicDashboard: {
    publicPayment: true,
    lastBillQuery: true,
    balanceQuery: true,
    procedures: true,
    claims: true,
    complaints: true,
    pendingFineAmountQuery: false,
    pendingWarrantyDepositQuery: false,
    socialRate: true,
    home: true,
    changeDueDate: true,
    usePublicBanner: true,
    simulator: true
  },

  homeButtonWithClientNumber: false,

  clientSummary: {
    selectAllAccounts: false,
    multipleAccountsPayment: false,
    useStatusColumn: false
  },

  forms: {
    showFormCategoriesListTitle: false
  },
  socialRate: {
    useSocialRate: true,
    content: [
      {
        title: '¿En qué consiste?',
        items: [
          {
            content:
              'La Tarifa Social es un beneficio otorgado por el gobierno nacional para usuarios residenciales en situación de vulnerabilidad que permite acceder a los servicios públicos a un precio más bajo.',
            id: 1
          }
        ],
        withBullet: false,
        color: 'lightBlue',
        sideImage: `${process.env.REACT_APP_ASSETS_BASE_URL}eden/socialRateIcon1.png`
      },
      {
        title: '¿Quiénes acceden a la Tarifa  Social?',
        items: [
          {
            content:
              'Pueden acceder quienes sean jubilados, pensionados o trabajadores en relación de dependencia o monotributistas que perciban una remuneración bruta menor o igual a dos salarios mínimos, titular de programas sociales, empleadas domésticas, beneficiarios de seguro de desempleo, veteranos de Malvinas, discapacitados, electrodependientes.',
            id: 2
          },
          {
            content:
              'Quedan excluidas aquellas personas que sean propietarias de más de un inmueble, posean un vehículo de hasta 10 años de antigüedad (excepto quienes posean certificado de discapacidad o electrodependencia), tengan aeronaves o embarcaciones de lujo y, luego de un período de 3 meses, hogares cuyo titular haya fallecido.',
            id: 2
          }
        ],
        withBullet: false,
        color: 'blue',
        sideImage: `${process.env.REACT_APP_ASSETS_BASE_URL}eden/socialRateIcon2.png`
      },
      {
        title: '¿Cómo se accede a la Tarifa Social?',
        items: [
          {
            content:
              'Es otorgada de manera automática mediante el cruce de datos de usuarios registrados en las empresas de servicios públicos de electricidad y gas por redes que realiza SINTyS (Sistema de Identificación Nacional Tributario y Social).',
            id: 1
          },
          {
            content:
              'Si ha sido rechazado, en función del motivo de la exclusión, el usuario debe realizar un trámite ante la Dependencia que corresponda para obtener una constancia que acredite la inexactitud de la información registrada por el SINTyS.',
            id: 2
          },
          {
            content:
              'Podés consultar por tu inclusión en la Tarifa Social en la página web del Organismo de control de Energía Eléctrica de la Provincia de Buenos Aires: https://oceba.gba.gov.ar/tarifa_social/pordni',
            id: 3
          }
        ],
        withBullet: false,
        color: 'darkBlue',
        sideImage: `${process.env.REACT_APP_ASSETS_BASE_URL}eden/socialRateIcon3.png`
      }
    ],
    showClientNumber: false,
    showAddress: false,
    manualInput: true,
    showCancelButton: true
  },
  publicHomeWithRegister: true,
  publicHomeWithLogo: false,
  publicHomePage: [
    {
      content: 'Pagá tu factura online.'
    },
    {
      content: 'Realizá tus reclamos técnicos, consultas y trámites.'
    },
    {
      content: 'Chequeá tus consumos.'
    },
    {
      content: 'Conocé tips para ahorrar energía.'
    },
    {
      content: '¡Y mucho más!'
    }
  ],

  complaints: {
    includeInTechnicalService: false,
    useComplaints: true,
    complaintsForm: false,
    withCrimeAlert: false,
    withSubtitles: false,
    withBullets: false,
    withLine: false,
    squaredContactItems: false,
    content: [
      {
        title: '¿Qué es una conexión clandestina?',
        items: [
          {
            content:
              'Es una instalación que está conectada de manera irregular a la red de distribución de energía eléctrica.',
            id: 1
          }
        ],
        withBullet: false,
        color: 'lightBlue',
        sideImage: `${process.env.REACT_APP_ASSETS_BASE_URL}eden/complaintsIcon2.png`
      },
      {
        title: '¿Por qué es peligroso?',
        items: [
          {
            content:
              'Representan un riesgo para vos, tu familia y vecinos ya que no cuentan con las condiciones de seguridad necesarias, provocando fallas en el servicio y/o accidentes.',
            id: 2
          }
        ],
        withBullet: false,
        color: 'blue',
        sideImage: `${process.env.REACT_APP_ASSETS_BASE_URL}eden/complaintsIcon1.png`
      }
    ],
    contactPhone: '0810-999-3336',
    contactMail: 'atencionalusuario@edensa.com.ar'
  },

  energyAdvisor: {
    design: DESIGN_OPTIONS[0],
    tipsWithTitle: false
  },

  changeDueDate: {
    active: false,
    content: [
      {
        title: 'Ahora podés elegir qué día pagar tu factura',
        items: [
          {
            content:
              'Si sos jubilado o pensionado titular del servicio eléctrico residencial, ahora vos podés decidir el día de pago que más te convenga según sea la fecha del depósito de tus haberes.',
            id: 1
          },
          {
            content:
              'El nuevo vencimiento entra en vigencia a partir de la primera factura que se emita desde la solicitud del cambio de vencimiento.',
            id: 2
          }
        ],
        withBullet: false,
        color: 'rgb(131, 187, 230)',
        sideIcon: `${process.env.REACT_APP_ASSETS_BASE_URL}eden/dueDateCalendar.svg`
      },
      {
        title: '¿Cómo realizar el cambio de fecha?',
        items: [
          {
            content: 'Llamanos al 0810 999 3336 de lunes a viernes de 8 a 18 hs.'
          }
        ],
        withBullet: false,
        color: 'rgb(51, 137, 202)',
        sideIcon: `${process.env.REACT_APP_ASSETS_BASE_URL}eden/dueDatePhone.svg`
      }
    ]
  },

  pendingWarrantyDepositQuery: {
    content: ''
  },

  pendingFineAmountQuery: {
    content: ''
  },

  paymentFlow: {
    useCardSelectionStep: false,
    provider: PAYMENT_GATEWAYS.FIRST_DATA,
    cardTypeOptions: [
      { id: 0, key: 'visa_debit', value: 'visa_debit', name: 'Visa Débito' },
      { id: 1, key: 'visa_credit', value: 'visa_credit', name: 'Visa Crédito' },
      { id: 2, key: 'master_credit', value: 'master_credit', name: 'Mastercard' },
      { id: 3, key: 'master_debit', value: 'master_debit', name: 'Maestro' },
      { id: 4, key: 'amex_credit', value: 'amex_credit', name: 'American Express' }
    ]
  },

  survey: {
    payments: false,
    procedures: false,
    claim: false
  },

  payments: {
    paymentForNis: false,
    paymentsWithEmail: true,
    paymentDocumentDownloadOnPDF: false,
    active: true,
    publicPayment: false,
    publicPaymentByNis: process.env.REACT_APP_ENV !== 'prod',
    multipleDownloadRow: false,
    paymentDocumentModal: true,
    columns: {
      checkbox: { show: true, width: 0.04, order: 1, showOnMobile: true },
      period: { show: true, width: 0.12, order: 2, showOnMobile: true },
      issued_on: { show: false, width: 0.09, order: 2, showOnMobile: false },
      document_type: { show: false, width: 0.18, order: 3, showOnMobile: false },
      document_number: { show: false, width: 0.13, order: 4, showOnMobile: false },
      first_expiration_on: { show: false, width: 0.11, order: 5, showOnMobile: false },
      status: { show: false, width: 0.14, order: 6, showOnMobile: false },
      amount: { show: false, width: 0.11, order: 7, showOnMobile: false },
      balance: { show: true, width: 0.12, order: 3, showOnMobile: true },
      download: { show: false, width: 0.12, order: 9, showOnMobile: false },
      barcode: { show: true, width: 0.2, order: 4, showOnMobile: true },
      external_id: { show: true, width: 0.13, order: 5, showOnMobile: true }
    }
  },

  balanceAndLastBillQuery: {
    showCancelButton: true
  },

  accountAlerts: {
    active: false,
    claimFormId: 72
  },

  alerts: {
    withoutIcon: true
  },

  accountPaymentDocsForm: {
    showCancelButton: true
  },

  imageResizing: {
    maxWidth: 1000,
    maxHeight: 1000,
    imageType: 'image/png'
  }
};

export default utilitySettings;
